/**
 * AdminVisualAssetStorage — admin home for every claymation visual
 * generated by the Buyer's Leverage content engine that isn't tied to a
 * specific article package.
 *
 * Today it manages two collections (`cards` + `strip_archive`) of the
 * Home page section visuals, but the page is built to scale: each
 * collection card is self-contained, so adding a future collection (e.g.
 * "social", "landing-experiments") only requires a backend plan and the
 * Mongo `collection_id` field.
 *
 * Per frame the admin can:
 *   • View the rendered image full-size
 *   • Inspect / edit the prompt
 *   • Regenerate (with or without prompt override)
 *   • Delete (next generate-all re-creates from the planned prompt)
 *
 * Per collection:
 *   • Trigger "Generate missing" (skips already-generated frames)
 *   • Trigger "Force regenerate all" (re-renders everything — $$$ warning)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft, Loader2, RefreshCw, Trash2, Sparkles, AlertTriangle,
    ImageIcon, ChevronDown, ChevronRight, Save, X, Download, HardDrive, Upload,
} from "lucide-react";
import { toast } from "sonner";
import {
    adminGetVisualAssetStorage,
    adminGenerateVisualAssetCollection,
    adminRegenerateVisualAssetFrame,
    adminDeleteVisualAssetFrame,
    adminUploadVisualAssetFrame,
} from "@/lib/api";

const STATUS_BADGE = {
    generated: "bg-[var(--bl-green)]/10 text-[var(--bl-green)] border-[var(--bl-green)]/30",
    generating: "bg-[var(--bl-amber)]/10 text-[var(--bl-amber)] border-[var(--bl-amber)]/30",
    pending: "bg-bl-bg text-bl-muted border-bl",
    failed: "bg-[var(--bl-rust)]/10 text-[var(--bl-rust)] border-[var(--bl-rust)]/30",
};

function bytesHuman(n) {
    if (!n || n < 1024) return `${n || 0} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}


function FrameTile({ collectionId, sectionId, frame, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [editing, setEditing] = useState(false);
    const [prompt, setPrompt] = useState(frame.prompt || "");
    const lastPromptRef = useRef(frame.prompt || "");
    const fileInputRef = useRef(null);
    const status = frame.generation_status || "pending";
    const isUserUpload = frame.source === "user_upload";
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const imgUrl = frame.public_url
        ? (frame.public_url.startsWith("/") ? `${base}${frame.public_url}` : frame.public_url)
        : null;

    // Re-sync local prompt state when the parent ships a fresh frame
    // payload (e.g. after a regenerate). Skip the no-op case so the
    // React Compiler rule doesn't flag this as a redundant setState.
    useEffect(() => {
        const next = frame.prompt || "";
        if (next === lastPromptRef.current) return;
        lastPromptRef.current = next;
        setPrompt(next);
    }, [frame.prompt]);

    const handleUploadClick = useCallback(() => {
        if (fileInputRef.current) fileInputRef.current.click();
    }, []);

    const handleFileChosen = useCallback(async (e) => {
        const file = e.target.files?.[0];
        // Always clear the input so picking the same file twice in a row
        // still fires onChange.
        e.target.value = "";
        if (!file) return;
        const MAX_MB = 10;
        if (file.size > MAX_MB * 1024 * 1024) {
            toast.error(`File is too large. Max ${MAX_MB} MB.`);
            return;
        }
        const allowed = ["image/png", "image/jpeg", "image/webp", "image/gif"];
        if (file.type && !allowed.includes(file.type)) {
            toast.error("Use PNG, JPEG, WebP, or GIF.");
            return;
        }
        setUploading(true);
        try {
            const r = await adminUploadVisualAssetFrame(
                collectionId, sectionId, frame.frame_index, file,
            );
            if (r?.ok) {
                toast.success(
                    `Uploaded ${file.name} → ${sectionId}/${frame.frame_index}`,
                );
                onChanged();
            } else {
                toast.error("Upload failed.");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed.");
        } finally {
            setUploading(false);
        }
    }, [collectionId, sectionId, frame.frame_index, onChanged]);

    const handleRegenerate = useCallback(async (withOverride) => {
        if (isUserUpload && !withOverride) {
            const confirmed = window.confirm(
                "This frame is a manual upload. Regenerating will replace "
                + "your uploaded image with a fresh AI render. Continue?",
            );
            if (!confirmed) return;
        }
        setBusy(true);
        try {
            const body = withOverride && prompt !== frame.prompt
                ? { prompt }
                : null;
            const r = await adminRegenerateVisualAssetFrame(
                collectionId, sectionId, frame.frame_index, body,
            );
            if (r?.ok) {
                toast.success(
                    `Regenerated ${collectionId}/${sectionId}/${frame.frame_index}`,
                );
            } else {
                toast.error(r?.error || "Regenerate failed.");
            }
            setEditing(false);
            onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Regenerate failed.");
        } finally {
            setBusy(false);
        }
    }, [collectionId, sectionId, frame.frame_index, frame.prompt, prompt, isUserUpload, onChanged]);

    const handleDelete = useCallback(async () => {
        if (!window.confirm(
            `Delete this frame? The next 'Generate missing' will recreate it from the planned prompt.`,
        )) return;
        setBusy(true);
        try {
            await adminDeleteVisualAssetFrame(
                collectionId, sectionId, frame.frame_index,
            );
            toast.success(`Deleted ${sectionId}/${frame.frame_index}`);
            onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed.");
        } finally {
            setBusy(false);
        }
    }, [collectionId, sectionId, frame.frame_index, onChanged]);

    return (
        <div
            className="bl-card overflow-hidden flex flex-col"
            data-testid={`vas-frame-${collectionId}-${sectionId}-${frame.frame_index}`}
        >
            <div className="relative aspect-[4/3] bg-bl-bg">
                {imgUrl ? (
                    <img
                        src={imgUrl}
                        alt={`${sectionId} frame ${frame.frame_index}`}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-bl-muted text-xs">
                        <ImageIcon className="h-6 w-6" />
                        {status === "generating" ? "Rendering…" : "Not yet generated"}
                    </div>
                )}
                <div className="absolute top-2 left-2 flex items-center gap-1">
                    <span
                        className={`text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full border ${STATUS_BADGE[status] || STATUS_BADGE.pending}`}
                    >
                        {status}
                    </span>
                    {isUserUpload && (
                        <span
                            className="text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full border bg-bl-primary/10 text-bl-primary border-bl-primary/30 flex items-center gap-1"
                            data-testid={`vas-frame-upload-badge-${collectionId}-${sectionId}-${frame.frame_index}`}
                            title="This frame was manually uploaded by an admin."
                        >
                            <Upload className="h-2.5 w-2.5" />
                            uploaded
                        </span>
                    )}
                </div>
                <div className="absolute top-2 right-2 text-[10px] text-bl-muted bg-bl-surface/85 backdrop-blur px-2 py-0.5 rounded-full border border-bl">
                    #{frame.frame_index}
                </div>
                {uploading && (
                    <div className="absolute inset-0 bg-bl-surface/80 backdrop-blur-sm flex items-center justify-center text-xs text-bl">
                        <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                        Uploading…
                    </div>
                )}
            </div>

            <div className="p-3 space-y-2 text-xs flex-1 flex flex-col">
                <div className="flex items-center justify-between text-bl-muted text-[10px]">
                    <span>{bytesHuman(frame.bytes)}</span>
                    <span>{frame.duration_ms ? `${frame.duration_ms} ms` : ""}</span>
                </div>

                {!editing ? (
                    <p
                        className="text-bl-muted line-clamp-3 leading-relaxed cursor-text"
                        onClick={() => setEditing(true)}
                        title="Click to edit prompt"
                    >
                        {prompt || <em>No prompt</em>}
                    </p>
                ) : (
                    <textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        rows={6}
                        className="w-full text-xs p-2 border border-bl rounded bg-bl-surface text-bl resize-none"
                        data-testid={`vas-frame-prompt-${collectionId}-${sectionId}-${frame.frame_index}`}
                    />
                )}

                {frame.error_message && (
                    <div className="text-[10px] text-[var(--bl-rust)] flex items-start gap-1">
                        <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                        {frame.error_message}
                    </div>
                )}

                <div className="mt-auto flex items-center gap-1.5 flex-wrap pt-2 border-t border-bl">
                    {!editing ? (
                        <>
                            <button
                                type="button"
                                onClick={handleUploadClick}
                                disabled={busy || uploading}
                                className="bl-btn-ghost text-[11px] gap-1 border border-bl-primary text-bl-primary"
                                data-testid={`vas-frame-upload-${collectionId}-${sectionId}-${frame.frame_index}`}
                                title="Upload an image from your computer to replace this frame"
                            >
                                {uploading
                                    ? <Loader2 className="h-3 w-3 animate-spin" />
                                    : <Upload className="h-3 w-3" />}
                                Upload
                            </button>
                            <button
                                type="button"
                                onClick={() => handleRegenerate(false)}
                                disabled={busy || uploading}
                                className="bl-btn-ghost text-[11px] gap-1 border border-bl"
                                data-testid={`vas-frame-regenerate-${collectionId}-${sectionId}-${frame.frame_index}`}
                            >
                                {busy
                                    ? <Loader2 className="h-3 w-3 animate-spin" />
                                    : <RefreshCw className="h-3 w-3" />}
                                Regenerate
                            </button>
                            {imgUrl && (
                                <a
                                    href={imgUrl}
                                    download={`${collectionId}_${sectionId}_${frame.frame_index}.png`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="bl-btn-ghost text-[11px] gap-1 border border-bl"
                                    data-testid={`vas-frame-download-${collectionId}-${sectionId}-${frame.frame_index}`}
                                >
                                    <Download className="h-3 w-3" />
                                    Download
                                </a>
                            )}
                            <button
                                type="button"
                                onClick={handleDelete}
                                disabled={busy || uploading}
                                className="bl-btn-ghost text-[11px] gap-1 text-bl-muted ml-auto"
                                data-testid={`vas-frame-delete-${collectionId}-${sectionId}-${frame.frame_index}`}
                            >
                                <Trash2 className="h-3 w-3" />
                                Delete
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={() => handleRegenerate(true)}
                                disabled={busy}
                                className="bl-btn-primary text-[11px] gap-1"
                                data-testid={`vas-frame-save-${collectionId}-${sectionId}-${frame.frame_index}`}
                            >
                                {busy
                                    ? <Loader2 className="h-3 w-3 animate-spin" />
                                    : <Save className="h-3 w-3" />}
                                Save &amp; regenerate
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setEditing(false);
                                    setPrompt(frame.prompt || "");
                                }}
                                disabled={busy}
                                className="bl-btn-ghost text-[11px] gap-1 border border-bl"
                            >
                                <X className="h-3 w-3" />
                                Cancel
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Hidden file input — opened by the Upload button. Accept
                covers the four content-types the backend sniffer trusts. */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={handleFileChosen}
                className="hidden"
                data-testid={`vas-frame-upload-input-${collectionId}-${sectionId}-${frame.frame_index}`}
            />
        </div>
    );
}


function CollectionPanel({ data, onChanged }) {
    const [open, setOpen] = useState(data.is_active);
    const [busyAction, setBusyAction] = useState(null);

    const generate = useCallback(async (force) => {
        setBusyAction(force ? "force" : "missing");
        try {
            const r = await adminGenerateVisualAssetCollection(
                data.collection_id, force,
            );
            toast.success(
                `${data.collection_id}: rendered ${r.rendered}, skipped ${r.skipped}, failed ${r.failed}`,
            );
            onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Generation failed.");
        } finally {
            setBusyAction(null);
        }
    }, [data.collection_id, onChanged]);

    const totalCost = useMemo(() => {
        // Rough back-of-envelope: $0.039 per generated frame, Nano Banana.
        return ((data.totals?.generated || 0) * 0.039).toFixed(2);
    }, [data.totals]);

    return (
        <div
            className="bl-card"
            data-testid={`vas-collection-${data.collection_id}`}
        >
            <div className="px-4 py-3 flex items-center gap-3 flex-wrap border-b border-bl">
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    className="flex items-center gap-2 text-left flex-1 min-w-0"
                    data-testid={`vas-collection-toggle-${data.collection_id}`}
                >
                    {open
                        ? <ChevronDown className="h-4 w-4 text-bl-muted" />
                        : <ChevronRight className="h-4 w-4 text-bl-muted" />}
                    <div className="min-w-0">
                        <div className="font-display font-semibold text-bl text-sm flex items-center gap-2">
                            {data.label}
                            {data.is_active && (
                                <span className="text-[10px] bg-bl-primary/10 text-bl-primary px-1.5 py-0.5 rounded-full uppercase tracking-wide">
                                    live on home
                                </span>
                            )}
                        </div>
                        <div className="text-[11px] text-bl-muted">
                            {data.totals.generated} / {data.plan_total} generated
                            {data.totals.failed > 0 && (
                                <span className="text-[var(--bl-rust)]">
                                    {" "}· {data.totals.failed} failed
                                </span>
                            )}
                            {" "}· est. spend ${totalCost}
                        </div>
                    </div>
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={() => generate(false)}
                        disabled={busyAction !== null}
                        className="bl-btn-ghost text-xs gap-1.5 border border-bl-primary text-bl-primary"
                        data-testid={`vas-generate-missing-${data.collection_id}`}
                    >
                        {busyAction === "missing"
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <Sparkles className="h-3 w-3" />}
                        Generate missing
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            if (window.confirm(
                                `Force-regenerate every frame in '${data.collection_id}'? `
                                + `Cost ~$${(data.plan_total * 0.039).toFixed(2)}.`,
                            )) generate(true);
                        }}
                        disabled={busyAction !== null}
                        className="bl-btn-ghost text-xs gap-1.5 border border-[var(--bl-rust)]/40 text-[var(--bl-rust)]"
                        data-testid={`vas-force-regen-${data.collection_id}`}
                    >
                        {busyAction === "force"
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <RefreshCw className="h-3 w-3" />}
                        Force regenerate all
                    </button>
                </div>
            </div>

            {open && (
                <div className="p-4 space-y-6">
                    {data.sections.map((s) => (
                        <div
                            key={s.section_id}
                            data-testid={`vas-section-${data.collection_id}-${s.section_id}`}
                        >
                            <div className="text-xs uppercase tracking-wider text-bl-muted mb-2 font-medium">
                                {s.title}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {s.frames.map((f) => (
                                    <FrameTile
                                        key={`${s.section_id}-${f.frame_index}`}
                                        collectionId={data.collection_id}
                                        sectionId={s.section_id}
                                        frame={f}
                                        onChanged={onChanged}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


export default function AdminVisualAssetStorage() {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await adminGetVisualAssetStorage();
                if (!alive) return;
                setState(r);
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Failed to load.");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [refreshTick]);

    const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

    return (
        <div
            className="container max-w-6xl mx-auto px-4 py-8 space-y-6"
            data-testid="admin-visual-asset-storage-page"
        >
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <Link
                        to="/admin/content"
                        className="text-xs text-bl-muted inline-flex items-center gap-1 mb-2 hover:text-bl"
                    >
                        <ArrowLeft className="h-3 w-3" />
                        Back to Content Packages
                    </Link>
                    <h1 className="font-display text-2xl font-semibold text-bl flex items-center gap-2">
                        <HardDrive className="h-6 w-6" />
                        Visual Asset Storage
                    </h1>
                    <p className="text-xs text-bl-muted mt-1 max-w-xl">
                        Claymation visuals managed by the content engine — the
                        active row of three cards under each home-page section
                        plus archived sets that are no longer in production
                        but kept for re-use. Edit a prompt inline, regenerate
                        any frame, or wipe the whole set and start fresh.
                    </p>
                </div>
                {state?.storage && (
                    <div
                        className="text-[11px] text-bl-muted text-right"
                        data-testid="vas-storage-stats"
                    >
                        <div>
                            {state.storage.file_count} files ·{" "}
                            {bytesHuman(state.storage.total_bytes)}
                        </div>
                        <div className="font-mono text-[10px]">
                            {state.storage.storage_dir}
                        </div>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="text-center py-12 text-bl-muted text-sm">
                    <Loader2 className="h-5 w-5 animate-spin inline" />
                    {" "}Loading visual assets…
                </div>
            ) : !state ? (
                <div className="bl-card p-8 text-center text-sm text-bl-muted">
                    Failed to load. Refresh the page.
                </div>
            ) : (
                <div className="space-y-4">
                    {Object.values(state.collections).map((c) => (
                        <CollectionPanel
                            key={c.collection_id}
                            data={c}
                            onChanged={refresh}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
